import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTestBenchStore } from '../stores/testBenchStore';
import { useConfigStore } from '../stores/configStore';
import { runCost, fmtCost } from '../utils/benchCost';
import './TestBenchPage.css';

/**
 * Prompt Test Bench — standalone page (was "Mode B" inside the Model Test
 * Bench). One fixed model, two system-prompt versions (A defaults to the live
 * R2 prompt, B is the operator's tweak). Both run through the real stage on
 * each selected word; outputs are judged BLIND, then labels reveal and the
 * winner is saved. Reuses the Model Test Bench's panels and styling so the two
 * pages read as siblings. Flow: content type → model → words → versions → run
 * → blind A/B results.
 */
export default function PromptBenchPage() {
  const {
    contentTypes,
    models,
    availableWords,
    loadingWords,
    loadingModels,
    error,
    fetchContentTypes,
    fetchModels,
    fetchAvailableWords,
    fetchStagePrompt,
    runPromptBench,
    pickWinner,
    promptPairs,
    runningPrompt,
    promptRunProgress,
    stopPromptRun,
    clearError,
  } = useTestBenchStore();
  const { modelPricing, fetchModelPricing } = useConfigStore();

  const [contentType, setContentType] = useState<string>('');
  const [modelId, setModelId] = useState<string>('');
  const [selectedWords, setSelectedWords] = useState<Set<string>>(new Set());
  const [wordSearch, setWordSearch] = useState<string>('');
  const [labelA, setLabelA] = useState<string>('Current');
  const [labelB, setLabelB] = useState<string>('Tweak');
  const [systemA, setSystemA] = useState<string>('');
  const [systemB, setSystemB] = useState<string>('');
  const [loadingPrompt, setLoadingPrompt] = useState<boolean>(false);

  useEffect(() => {
    fetchContentTypes();
    fetchModels();
    fetchAvailableWords();
    fetchModelPricing();
  }, [fetchContentTypes, fetchModels, fetchAvailableWords, fetchModelPricing]);

  useEffect(() => {
    if (!contentType && contentTypes.length > 0) setContentType(contentTypes[0].key);
  }, [contentType, contentTypes]);
  useEffect(() => {
    if (!modelId && models.length > 0) setModelId(models[0].id || '');
  }, [modelId, models]);

  const activeCt = contentTypes.find((c) => c.key === contentType) || null;

  const loadCurrent = useCallback(async () => {
    if (!contentType) return;
    setLoadingPrompt(true);
    const p = await fetchStagePrompt(contentType);
    setLoadingPrompt(false);
    if (p) {
      setSystemA(p.system);
      if (!systemB.trim()) setSystemB(p.system);
    }
  }, [contentType, fetchStagePrompt, systemB]);

  const filteredWords = useMemo(() => {
    const q = wordSearch.trim().toLowerCase();
    return q ? availableWords.filter((w) => w.word.toLowerCase().includes(q)) : availableWords;
  }, [availableWords, wordSearch]);

  const toggleWord = useCallback((id: string) => {
    setSelectedWords((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const canRun =
    !!contentType && !!modelId && selectedWords.size > 0 &&
    systemA.trim().length > 0 && systemB.trim().length > 0 && !runningPrompt;

  const run = useCallback(() => {
    void runPromptBench({
      contentType,
      modelId,
      wordIds: Array.from(selectedWords),
      versionA: { label: labelA || 'A', system: systemA },
      versionB: { label: labelB || 'B', system: systemB },
    });
  }, [runPromptBench, contentType, modelId, selectedWords, labelA, labelB, systemA, systemB]);

  return (
    <div className="testbench-page">
      <div className="testbench-header">
        <h1 className="testbench-title">Prompt Test Bench</h1>
        <p className="testbench-subtitle">
          Fixed model, two prompt versions, run on real words through the real stage.
          Outputs are shown <strong>blind</strong> — pick the winner on quality, then the labels reveal.
        </p>
      </div>

      {error && (
        <div className="testbench-error">
          <span>{error}</span>
          <button onClick={clearError}>Dismiss</button>
        </div>
      )}

      <div className="testbench-controls tb-controls-stack">
        {/* 1. Content type */}
        <section className="tb-panel">
          <h2 className="tb-panel-title">Content Type</h2>
          <select className="tb-input" value={contentType} onChange={(e) => setContentType(e.target.value)}>
            {contentTypes.length === 0 && <option value="">Loading…</option>}
            {contentTypes.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          {activeCt && (
            <p className="tb-ct-meta">
              Stage <code>{activeCt.stageId}</code> &middot; surfaces <code>{activeCt.fields.join(', ')}</code>
            </p>
          )}
        </section>

        {/* 2. Model */}
        <section className="tb-panel">
          <h2 className="tb-panel-title">Model</h2>
          <p className="tb-panel-hint">Both prompt versions run on this one model, so the prompt is the only variable.</p>
          {loadingModels ? (
            <p className="tb-empty">Loading registry…</p>
          ) : (
            <select className="tb-input" value={modelId} onChange={(e) => setModelId(e.target.value)}>
              {models.filter((m) => m.id).map((m) => (
                <option key={m.id} value={m.id}>{m.displayName}</option>
              ))}
            </select>
          )}
        </section>

        {/* 3. Words */}
        <section className="tb-panel">
          <h2 className="tb-panel-title">Words <span className="tb-count">{selectedWords.size}</span></h2>
          <p className="tb-panel-hint">Real words; their records fill the stage template like production.</p>
          <input
            className="tb-input"
            type="text"
            value={wordSearch}
            placeholder="Search words…"
            onChange={(e) => setWordSearch(e.target.value)}
          />
          <div className="tb-model-select-actions">
            <button className="tb-link-btn" onClick={() => setSelectedWords(new Set(filteredWords.map((w) => w.id)))}>Select shown</button>
            <button className="tb-link-btn" onClick={() => setSelectedWords(new Set())}>Clear</button>
          </div>
          {loadingWords ? (
            <p className="tb-empty">Loading words…</p>
          ) : filteredWords.length === 0 ? (
            <p className="tb-empty">No matching words.</p>
          ) : (
            <div className="tb-model-list tb-word-list">
              {filteredWords.slice(0, 200).map((w) => {
                const checked = selectedWords.has(w.id);
                return (
                  <label key={w.id} className={`tb-model-item ${checked ? 'checked' : ''}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleWord(w.id)} />
                    <span className="tb-model-name">{w.word}</span>
                  </label>
                );
              })}
            </div>
          )}
        </section>

        {/* 4. Prompt versions */}
        <section className="tb-panel tb-prompt-versions">
          <div className="tb-version-head">
            <h2 className="tb-panel-title">Prompt Versions</h2>
            <button className="tb-link-btn" onClick={loadCurrent} disabled={loadingPrompt || !contentType}>
              {loadingPrompt ? 'Loading…' : 'Load current prompt into A'}
            </button>
          </div>
          <label className="tb-version-label">
            Version A label
            <input className="tb-input" value={labelA} onChange={(e) => setLabelA(e.target.value)} />
          </label>
          <textarea
            className="tb-prompt-text"
            value={systemA}
            onChange={(e) => setSystemA(e.target.value)}
            placeholder="Version A system prompt (load the current one, or paste yours)"
            rows={8}
          />
          <label className="tb-version-label">
            Version B label
            <input className="tb-input" value={labelB} onChange={(e) => setLabelB(e.target.value)} />
          </label>
          <textarea
            className="tb-prompt-text"
            value={systemB}
            onChange={(e) => setSystemB(e.target.value)}
            placeholder="Version B system prompt (your tweak)"
            rows={8}
          />
        </section>
      </div>

      {/* 5. Run */}
      <div className="tb-run-bar">
        <button className="tb-btn-run" onClick={run} disabled={!canRun}>
          {runningPrompt ? 'Running A/B…' : 'Run A/B'}
        </button>
        {runningPrompt && (
          <button className="tb-btn-stop" onClick={() => void stopPromptRun()} title="Stop the run — remaining calls are cancelled; finished pairs are kept">
            Stop
          </button>
        )}
        <span className="tb-run-note">
          {selectedWords.size > 0
            ? `${selectedWords.size} word${selectedWords.size !== 1 ? 's' : ''} × 2 versions = ${selectedWords.size * 2} paid calls. Web search is enabled for models that support it, matching production — search calls add cost.`
            : 'Pick a content type, a model, at least one word, and both prompt versions.'}
        </span>
      </div>

      {runningPrompt && promptRunProgress && (
        <div className="tb-progress" role="status" aria-live="polite">
          <span className="tb-progress-spinner" aria-hidden="true" />
          <span className="tb-progress-count">
            {promptRunProgress.done} of {promptRunProgress.total} calls done
          </span>
          {promptRunProgress.current && (
            <span className="tb-progress-current">— running: {promptRunProgress.current}</span>
          )}
          <span className="tb-progress-bar">
            <span
              className="tb-progress-fill"
              style={{ width: `${promptRunProgress.total ? Math.round((promptRunProgress.done / promptRunProgress.total) * 100) : 0}%` }}
            />
          </span>
        </div>
      )}

      {/* 6. Blind A/B results */}
      {promptPairs.length > 0 && (
        <section className="tb-results">
          <h2 className="tb-panel-title">Blind A/B — pick the better output</h2>
          {promptPairs.map((pair) => {
            const decided = pair.panels.some((p) => p.winner);
            return (
              <div key={pair.pairId} className="tb-pair">
                <div className="tb-pair-word">{pair.word}</div>
                <div className="tb-pair-panels">
                  {pair.panels.map((panel, idx) => {
                    const isWinner = decided && panel.winner === panel.promptVersion;
                    return (
                      <div key={panel.id} className={`tb-pair-panel ${isWinner ? 'winner' : ''}`}>
                        <div className="tb-pair-panel-head">
                          <span>{decided ? `${panel.promptLabel} (${panel.promptVersion})` : `Output ${idx + 1}`}</span>
                          <span className="tb-pair-panel-meta">
                            {panel.ok && <span className="tb-pair-cost">{fmtCost(runCost(panel, modelPricing))}</span>}
                            {isWinner && <span className="tb-winner-badge">winner</span>}
                          </span>
                        </div>
                        <pre className="tb-pair-output">
                          {panel.ok
                            ? (panel.surfaced && Object.keys(panel.surfaced).length
                                ? Object.entries(panel.surfaced).map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join('\n\n')
                                : (panel.output || '(empty)'))
                            : `error: ${panel.error || 'failed'}`}
                        </pre>
                        {!decided && (
                          <button className="tb-btn-secondary" onClick={() => pickWinner(pair.pairId, panel.promptVersion)}>
                            Pick this
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}
